import React, { useState, useEffect, useMemo, useRef } from "react";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import {
    Grid,
    Card,
    TextField,
    InputAdornment,
    CircularProgress,
    Box,
    Button, Select,
    MenuItem, InputLabel, FormControl,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PublicIcon from "@mui/icons-material/Public";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import PeopleIcon from "@mui/icons-material/People";
import RfqFormDialog from "./RfqFormDialog";
import {Bar, Doughnut, Line} from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { alpha } from "@mui/material/styles";
import DataTable from "examples/Tables/DataTable";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import { useNavigate } from "react-router-dom";
import countryOptions from "../../components/List/Countries";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

// Döviz kurları
const FX_RATES = {
    USD: 1,
    EUR: 1.1,
    TRY: 0.03,
    GBP: 1.25,
};

const STATUS_META = {
    pending:  { label: "Pending",     color: "#FFB300" }, // amber
    sent:     { label: "Sent",        color: "#1E88E5" }, // blue
   /* progress: { label: "In Progress", color: "#8E24AA" }, // purple
    secured:  { label: "Secured",     color: "#43A047" }, // green*/
    cancelled:     { label: "Cancelled",        color: "#E53935" }, // red
    closed:   { label: "Closed",      color: "#546E7A" }, // grey-blue
};

// Artık seçilemeyen ama eski kayıtlarda bulunan durumlar. Eskiden bunlar "Pending" olarak
// görünüyordu (kazanılmış/kaybedilmiş bir RFQ beklemede gibi duruyordu); şimdi doğru adıyla gösterilir.
const LEGACY_STATUS_META = {
    progress: { label: "In Progress", color: "#8E24AA" },
    secured: { label: "Secured", color: "#43A047" },
    lost: { label: "Lost", color: "#6D4C41" },
};
const getStatusMeta = (key) =>
    STATUS_META[key] ||
    LEGACY_STATUS_META[key] || {
        label: key ? String(key).charAt(0).toUpperCase() + String(key).slice(1) : STATUS_META.pending.label,
        color: "#9E9E9E",
    };

const StatusDot = ({ status }) => {
    const key = String(status || "pending").toLowerCase();
    const meta = getStatusMeta(key);

    return (
        <span
            style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: meta.color,
                marginRight: 8,
                verticalAlign: "middle",
            }}
        />
    );
};

const normalizeOffer = (amount, currency) => {
    if (!amount) return 0;
    const rate = FX_RATES[currency?.toUpperCase()] || 1;
    return amount * rate;
};

// ✅ HQ + Service Country birleşimi
const getAgentHQCountries = (ga) => {
    if (!ga) return [];
    const hqRaw =
        ga.globalAgentCountry ?? ga.hqCountry ?? ga.country ?? ga.originCountry ?? ga;
    const serviceRaw = ga.servicesCountry ?? ga.serviceCountry ?? ga.serviceCountries;

    const extract = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map(String);
        if (typeof raw === "object")
            return Object.values(raw).map(String).filter(Boolean);
        return [String(raw)];
    };

    const hqList = extract(hqRaw);
    const svcList = extract(serviceRaw);
    return Array.from(new Set([...hqList, ...svcList].filter(Boolean)));
};


const convertOffer = (amount, fromCurrency, toCurrency) => {
    if (!amount) return 0;
    const fromRate = FX_RATES[fromCurrency?.toUpperCase()] || 1;
    const toRate = FX_RATES[toCurrency?.toUpperCase()] || 1;
    return (amount * fromRate) / toRate;
};

// 📊 Ortak Chart Options
const chartOptions = (type = "doughnut", onClickHandler) => ({
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
        legend: {
            position: "bottom",
            fullSize: false,
            labels: {
                boxWidth: 14,
                padding: 12,
                font: { size: 12 },
            },
        },
    },
    layout: { padding: { bottom: 20 } },
    onClick: (event, elements, chart) => {
        if (!elements || elements.length === 0) return;
        const index = elements[0]?.index;
        const labels = chart?.data?.labels;
        if (!labels || !labels[index]) return;

        const label = String(labels[index] || "").trim();
        if (label && onClickHandler) {
            onClickHandler(label);
        }
    },
    ...(type === "bar" && {
        scales: {
            x: { grid: { display: false } },
            y: { grid: { color: "#eee" } },
        },
    }),
});

// 🎨 Chip renkleri tipine göre
const SAFE_CHIP_COLORS = new Set([
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
]);

const safeChipColor = (c) => (SAFE_CHIP_COLORS.has(c) ? c : "default");

const chipColor = (type) => {
    switch (String(type || "").trim()) {
        case "status":
            return "secondary";
        case "agent":
            return "info";
        case "origin":
            return "success";
        case "destination":
            return "warning";
        case "month":
            return "primary";
        case "serviceMode":
            return "error";
        case "transportationMode":
            return "error";
        case "customer":
            return "info";
        default:
            return "default";
    }
};

const COUNTRY_ALIASES = {
    USA: "United States",
    US: "United States",
    "United States of America": "United States",
    UK: "United Kingdom",
    Turkey: "Türkiye",
    Türkiye: "Türkiye",
};

const getCountryLabel = (o) =>
    typeof o === "string"
        ? o
        : o?.label ?? o?.name ?? o?.value ?? o?.title ?? o?.country ?? "";

const canonicalCountry = (name) => {
    if (!name) return "";
    const t = String(name).trim();
    return COUNTRY_ALIASES[t] || t;
};

const normalizeCountry = (s) =>
    canonicalCountry(
        String(s || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
    )
        .toLowerCase()
        .trim();

const getEndpointSide = (serviceMode, side) => {
    if (!serviceMode) return null;
    const value = String(serviceMode).toLowerCase();

    const startsDoor = value.startsWith("door");
    const endsDoor = value.endsWith("door");

    if (side === "origin") return startsDoor ? "door" : "port";
    return endsDoor ? "door" : "port";
};

const getLocationKind = (transportationMode, serviceMode, side) => {
    const tm = String(transportationMode || "");

    if (tm === "Road FTL" || tm === "Road LTL") return "city";

    const endpoint = getEndpointSide(serviceMode, side);
    if (!endpoint) return null;

    if (tm === "Ocean FCL" || tm === "Ocean LCL" || tm === "Project") {
        return endpoint === "door" ? "city" : "seaport";
    }

    if (tm === "Air") {
        return endpoint === "door" ? "city" : "airport";
    }

    return null;
};

export default function RFQDashboard({ embedded = false }) {
    const [rfqList, setRfqList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalSearch, setGlobalSearch] = useState("");
    const [chartFilters, setChartFilters] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState("USD");
    const navigate = useNavigate();
    const [updatingStatusIds, setUpdatingStatusIds] = useState(() => new Set());
    const [rfqDateStart, setRfqDateStart] = useState("");
    const [rfqDateEnd, setRfqDateEnd] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [requestForm, setRequestForm] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [salesRepOptions, setSalesRepOptions] = useState([]);

    const handleOpenRfqPopup = (row) => {
        const nextForm = {
            ...row.request,
            ...row,
            id: String(row.id || row._id),
            rfqNumber: row.rfqNumber || row?.request?.rfqNumber || "—",
            companies: row.companies || row?.request?.companies || [],
            salesRepIds: Array.isArray(row.salesRepIds || row?.request?.salesRepIds)
                ? row.salesRepIds || row?.request?.salesRepIds
                : [],
        };

        setRequestForm(nextForm);
        setSelectedCustomer(row.customer || null);
        setOpenDialog(true);
    };

    const optionsCache = useRef(new Map());
    const [originOptions, setOriginOptions] = useState([]);
    const [destOptions, setDestOptions] = useState([]);
    const [originLoading, setOriginLoading] = useState(false);
    const [destLoading, setDestLoading] = useState(false);

    const findCountryOption = (label) => {
        const want = normalizeCountry(label);

        return (
            (countryOptions || []).find(
                (c) => normalizeCountry(getCountryLabel(c)) === want
            ) || null
        );
    };

    const originKind = useMemo(
        () =>
            getLocationKind(
                requestForm?.transportationMode,
                requestForm?.serviceMode,
                "origin"
            ),
        [requestForm?.transportationMode, requestForm?.serviceMode]
    );

    const destinationKind = useMemo(
        () =>
            getLocationKind(
                requestForm?.transportationMode,
                requestForm?.serviceMode,
                "destination"
            ),
        [requestForm?.transportationMode, requestForm?.serviceMode]
    );

    const normalizeOptions = (payload) => {
        const raw =
            payload?.data?.data ||
            payload?.data ||
            payload?.items ||
            payload ||
            [];

        return (Array.isArray(raw) ? raw : [])
            .map((x) =>
                typeof x === "string"
                    ? x
                    : x?.name ?? x?.label ?? x?.title ?? x?.value
            )
            .filter(Boolean);
    };

    const fetchOptions = async (country, kind) => {
        if (!country || !kind) return [];

        const cacheKey = `${kind}:${country}`;
        const cached = optionsCache.current.get(cacheKey);

        if (cached?.length) return cached;

        let res;

        if (kind === "city") {
            res = await CrudService.getCitiesByCountry(country);
        } else if (kind === "seaport") {
            res = await CrudService.getSeaportsByCountry(country);
        } else if (kind === "airport") {
            res = await CrudService.getAirportsByCountry(country);
        } else {
            return [];
        }

        const list = normalizeOptions(res);
        optionsCache.current.set(cacheKey, list);

        return list;
    };

    useEffect(() => {
        if (!requestForm?.origin || !originKind) {
            setOriginOptions([]);
            return;
        }

        setOriginLoading(true);

        fetchOptions(requestForm.origin, originKind)
            .then(setOriginOptions)
            .finally(() => setOriginLoading(false));
    }, [requestForm?.origin, originKind]);

    useEffect(() => {
        if (!requestForm?.destination || !destinationKind) {
            setDestOptions([]);
            return;
        }

        setDestLoading(true);

        fetchOptions(requestForm.destination, destinationKind)
            .then(setDestOptions)
            .finally(() => setDestLoading(false));
    }, [requestForm?.destination, destinationKind]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const res = await CrudService.getCustomers();
                const raw = res?.data?.data || res?.data || [];

                const customers = (Array.isArray(raw) ? raw : []).map((item) => ({
                    id: String(item.id || item._id),
                    ...(item.attributes || item),
                }));

                setCustomerOptions(customers);
            } catch (err) {
                console.error("Customers could not be loaded", err);
            }
        };

        fetchCustomers();
    }, []);

    useEffect(() => {
        const fetchSalesReps = async () => {
            try {
                const res = await CrudService.getUsers();
                const raw = res?.data?.data || res?.data || [];

                const users = (Array.isArray(raw) ? raw : [])
                    .map((u) => ({
                        id: String(u.id || u._id),
                        label: u?.attributes?.name || u?.name || "",
                    }))
                    .filter((u) => u.id && u.label);

                setSalesRepOptions(users);
            } catch (err) {
                console.error("Sales reps could not be loaded", err);
            }
        };

        fetchSalesReps();
    }, []);

    const fetchRfqs = async () => {
        try {
            setLoading(true);

            const res = await CrudService.getSentRequests({
                "page[size]": 1000,
                "page[number]": 1,
            });

            const arr = res?.data?.data || res?.data || [];

            const normalized = arr.map((item) => {
                const offer = item.offer ? Number(item.offer) : 0;
                const currency = item.offerCurrency || "USD";
                const createdAt = item.created_at || item.createdAt;
                const month = createdAt ? dayjs(createdAt).format("YYYY-MM") : "Unknown";

                const hqCountries = getAgentHQCountries(item);
                const origin = item.origin || item?.request?.origin || hqCountries[0] || "Unknown";
                const destination = item.destination || item?.request?.destination || hqCountries[1] || "Unknown";

                return {
                    ...item.request,
                    ...item,
                    id: item.id || item._id,
                    rfqNumber: item.rfqNumber || item?.request?.rfqNumber || "—",
                    origin,
                    destination,
                    hqCountries,
                    transportationMode: item.transportationMode || item?.request?.transportationMode,
                    serviceMode: item.serviceMode || item?.request?.serviceMode,
                    incoterm: item.incoterm || item?.request?.incoterm,
                    status: item.status || item?.request?.status,
                    offer,
                    offerCurrency: currency,
                    offerUsd: normalizeOffer(offer, currency),
                    created_at: createdAt,
                    month,
                    customer: item.customer || item?.request?.customer || null,
                    request: item.request || {},
                    salesRepIds: item.salesRepIds || item?.request?.salesRepIds || [],
                };
            });

            setRfqList(normalized);
        } catch (err) {
            console.error("Error fetching RFQs:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRfqs();
    }, []);

    useEffect(() => {
        const handleFocus = () => {
            fetchRfqs();
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, []);

    const mapRfqTransportationModeToQuotingType = (tm) => {
        const t = String(tm || "");

        if (t.startsWith("Road")) return "Road";
        if (t.startsWith("Ocean")) return "Ocean";
        if (t === "Air") return "Air";
        if (t === "Project") return "ProjectCargo";

        return "";
    };

    const handleConnectQuote = async (form) => {
        const selectedTransportType = mapRfqTransportationModeToQuotingType(
            form.transportationMode
        );

        navigate("/quoting-tool/create-quoting-tool/details", {
            state: {
                selectedTransportType,
                prefill: form,
                rfq: form,
            },
        });

        setOpenDialog(false);
    };

    const updateRfqStatus = async (rowId, nextStatus) => {
        const id = String(rowId);
        const status = String(nextStatus || "pending").toLowerCase();

        // Optimistic UI: önce ekranda değiştir
        const prevList = rfqList;
        setRfqList((prev) =>
            prev.map((r) => (String(r.id) === id ? { ...r, status } : r))
        );

        setUpdatingStatusIds((prev) => new Set(prev).add(id));

        try {
            // ✅ Buradaki method adını kendi CrudService’ine göre ayarla.
            // Örnekler:
            // await CrudService.updateSentRequest(id, { status });
            // await CrudService.patchSentRequest(id, { status });
            // await CrudService.editSentRequest(id, { status });

            await CrudService.updateSentRequest(id, { status });
        } catch (err) {
            console.error("Status update failed:", err);
            // Hata olursa geri al
            setRfqList(prevList);
        } finally {
            setUpdatingStatusIds((prev) => {
                const copy = new Set(prev);
                copy.delete(id);
                return copy;
            });
        }
    };

    // Tablo kolonları
    const columns = [
        {
            Header: "RFQ No",
            accessor: "rfqNumber",
            Cell: ({ row, value }) => {
                const isTotals = row?.original?._isTotals;

                if (isTotals) {
                    return (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                                size="small"
                                label="TOTALS"
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    fontSize: "medium",
                                    bgcolor: "rgba(255,179,0,0.15)",
                                }}
                            />
                        </Box>
                    );
                }

                return (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => handleOpenRfqPopup(row.original)}
                        sx={{ textTransform: "none", p: 0, minWidth: "auto", fontWeight: 700 }}
                    >
                        {value || "—"}
                    </Button>
                );
            },
        },

        {
            Header: "Customer",
            accessor: "customer",
            Cell: ({ row, value }) => {
                if (row?.original?._isTotals) return "—";

                const name = value?.name || "—";
                const shortName = name.length > 10 ? `${name.slice(0, 10)}…` : name;

                return (
                    <Tooltip title={name} arrow>
                        <Chip label={shortName} size="small" color="info" />
                    </Tooltip>
                );
            },
        },

        {
            Header: "Origin",
            accessor: "origin",
            Cell: ({ row, value }) => (row?.original?._isTotals ? "—" : value || "—"),
        },

        {
            Header: "Destination",
            accessor: "destination",
            Cell: ({ row, value }) => (row?.original?._isTotals ? "—" : value || "—"),
        },

        {
            Header: "Service Mode",
            accessor: "serviceMode",
            Cell: ({ row, value }) => (row?.original?._isTotals ? "—" : value || "—"),
        },

        {
            Header: "Transportation Mode",
            accessor: "transportationMode",
            Cell: ({ row, value }) => (row?.original?._isTotals ? "—" : value || "—"),
        },

        {
            Header: "Status",
            accessor: "status",
            Cell: ({ row, value }) => {
                const isTotals = row?.original?._isTotals;
                if (isTotals) return "—";

                const rowId = row?.original?.id;
                const current = String(value || "pending").toLowerCase();
                const isUpdating = updatingStatusIds.has(String(rowId));

                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Select
                            size="small"
                            value={current}
                            disabled={!rowId || isUpdating}
                            onChange={(e) => updateRfqStatus(rowId, e.target.value)}
                            renderValue={(val) => {
                                const meta = getStatusMeta(val);
                                return (
                                    <Box display="flex" alignItems="center">
                                        <StatusDot status={val} />
                                        <span style={{ fontSize: 13, fontWeight: 700 }}>{meta.label}</span>
                                    </Box>
                                );
                            }}
                            sx={{
                                minWidth: 150,
                                borderRadius: 2,
                                "& .MuiSelect-select": { display: "flex", alignItems: "center" },
                            }}
                        >
                            {Object.keys(STATUS_META).map((key) => (
                                <MenuItem key={key} value={key}>
                                    <Box display="flex" alignItems="center">
                                        <StatusDot status={key} />
                                        <span style={{ fontSize: 13, fontWeight: 700 }}>
                    {STATUS_META[key].label}
                  </span>
                                    </Box>
                                </MenuItem>
                            ))}
                            {!STATUS_META[current] && (
                                <MenuItem value={current} disabled>
                                    <Box display="flex" alignItems="center">
                                        <StatusDot status={current} />
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>{getStatusMeta(current).label}</span>
                                    </Box>
                                </MenuItem>
                            )}
                        </Select>

                        {isUpdating && <CircularProgress size={16} />}
                    </Box>
                );
            },
        },

        {
            Header: "Requested Date",
            accessor: "etd",
            sortType: (rowA, rowB, columnId) => {
                const a = rowA.values[columnId] ? dayjs(rowA.values[columnId]).valueOf() : -Infinity;
                const b = rowB.values[columnId] ? dayjs(rowB.values[columnId]).valueOf() : -Infinity;
                return a - b;
            },
            Cell: ({ row, value }) => {
                if (row?.original?._isTotals) return "—";
                return value ? dayjs(value).locale("tr").format("DD/MM/YYYY") : "—";
            },
        },

        {
            Header: "RFQ Owner",
            accessor: "salesRepIds",
            Cell: ({ row, value }) => {
                if (row?.original?._isTotals) return "—";
                if (!Array.isArray(value) || !value.length) return "—";

                const names = value
                    .map((id) => salesRepOptions.find((u) => String(u.id) === String(id))?.label)
                    .filter(Boolean);

                return names.length ? names.join(", ") : "—";
            },
        },

        {
            Header: "Offer",
            accessor: "offer",
            Cell: ({ row }) => {
                const isTotals = row?.original?._isTotals;

                if (isTotals) {
                    const t = row.original.offerTotals;

                    return (
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            <Chip
                                size="small"
                                icon={<PublicIcon />}
                                label={`Total: ${Number(t?.totalInSelected ?? 0).toLocaleString(undefined, {
                                    maximumFractionDigits: 2,
                                })} ${selectedCurrency}`}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    fontSize: "medium",
                                    bgcolor: "rgba(84,110,122,0.10)",
                                }}
                            />

                        </Box>
                    );
                }

                const offer = Number(row.original?.offer || 0);
                const cur = row.original?.offerCurrency || "USD";
                return offer > 0 ? `${offer.toFixed(2)} ${cur}` : "—";
            },
        },
    ];

    // ✅ Tarih filtresi yardımcı
    const isWithinDateRange = (row) => {
        if (!rfqDateStart && !rfqDateEnd) return true;
        if (!row?.etd) return false;

        const d = dayjs(row.etd);
        if (rfqDateStart && d.isBefore(dayjs(rfqDateStart).startOf("day"))) return false;
        if (rfqDateEnd && d.isAfter(dayjs(rfqDateEnd).endOf("day"))) return false;
        return true;
    };

// 1) Sadece tarih filtresi (grafikler dahil her şeye baz olsun)
    const dateFilteredBase = useMemo(() => {
        return (rfqList || []).filter(isWithinDateRange);
    }, [rfqList, rfqDateStart, rfqDateEnd]);

// 2) Tarih + chartFilters (grafikler/KPI’lar buna baksın)
    const analyticsRows = useMemo(() => {
        return (dateFilteredBase || []).filter((row) => {
            for (const filter of (chartFilters || [])) {
                if (!filter?.type || !filter?.value) continue;

                if (filter.type === "origin" && row.origin !== filter.value) return false;
                if (filter.type === "destination" && row.destination !== filter.value) return false;
                if (filter.type === "status" && row.status !== filter.value) return false;
                if (filter.type === "serviceMode" && row.serviceMode !== filter.value) return false;
                if (filter.type === "transportationMode" && row.transportationMode !== filter.value) return false;

                if (filter.type === "customer" && (row.customer?.name || "Unknown") !== filter.value) return false;

                if (filter.type === "owner") {
                    const hasOwner =
                        Array.isArray(row.salesRepIds) &&
                        row.salesRepIds.some((id) =>
                            salesRepOptions.find((u) => String(u.id) === String(id))?.label === filter.value
                        );
                    if (!hasOwner) return false;
                }

                if (filter.type === "requestedMonth") {
                    if (!row.etd) return false;
                    const rowMonth = dayjs(row.etd).format("YYYY-MM");
                    if (rowMonth !== filter.value) return false;
                }

                // ✅ Weekly filter (ekliyoruz)
                if (filter.type === "requestedWeek") {
                    if (!row.etd) return false;
                    const rowWeek = dayjs(row.etd).format("GGGG-[W]WW"); // ISO week label
                    if (rowWeek !== filter.value) return false;
                }
            }
            return true;
        });
    }, [dateFilteredBase, chartFilters, salesRepOptions]);

    // KPI
    const totalRFQs = analyticsRows.length;

    const avgOffer = totalRFQs
        ? (analyticsRows.reduce((a, b) => a + (b.offerUsd || 0), 0) / totalRFQs).toLocaleString(undefined, {
            maximumFractionDigits: 3,
        })
        : "0";

    const uniqueOrigins = [...new Set(analyticsRows.map((r) => r.origin))].length;
    const uniqueDestinations = [...new Set(analyticsRows.map((r) => r.destination))].length;


    // Top Origins
    const topOrigins = useMemo(() => {
        const counts = {};
        analyticsRows.forEach((r) => {
            if (r.origin) {
                if (!counts[r.origin]) counts[r.origin] = { count: 0, total: 0 };
                counts[r.origin].count += 1;
                counts[r.origin].total += r.offerUsd || 0;
            }
        });
        return Object.entries(counts)
            .map(([label, v]) => ({ label, ...v }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [analyticsRows]);

    // Top Destinations
    const topDestinations = useMemo(() => {
        const counts = {};
        analyticsRows.forEach((r) => {
            if (r.destination) {
                if (!counts[r.destination])
                    counts[r.destination] = { count: 0, total: 0 };
                counts[r.destination].count += 1;
                counts[r.destination].total += r.offerConverted || 0;
            }
        });
        return Object.entries(counts)
            .map(([label, v]) => ({ label, ...v }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [analyticsRows]);

    const topOwners = useMemo(() => {
        const counts = {};

        analyticsRows.forEach((r) => {
            if (!Array.isArray(r.salesRepIds)) return;

            r.salesRepIds.forEach((id) => {
                const name = salesRepOptions.find(
                    (u) => String(u.id) === String(id)
                )?.label;

                if (!name) return;

                if (!counts[name]) counts[name] = 0;
                counts[name] += 1;
            });
        });

        return Object.entries(counts)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6); // top 6 owner
    }, [analyticsRows, salesRepOptions]);

// 3) Tablo için: tarih + chartFilters + globalSearch
    const tableRows = useMemo(() => {
        return (analyticsRows || []).filter((row) => {
            if (!globalSearch) return true;
            const q = globalSearch.trim().toLowerCase();

            return (
                row.rfqNumber?.toLowerCase().includes(q) ||
                row.customer?.name?.toLowerCase().includes(q) ||
                row.origin?.toLowerCase().includes(q) ||
                row.destination?.toLowerCase().includes(q) ||
                row.serviceMode?.toLowerCase().includes(q) ||
                row.transportationMode?.toLowerCase().includes(q) ||
                row.status?.toLowerCase().includes(q) ||
                (Array.isArray(row.salesRepIds) &&
                    row.salesRepIds.some((id) =>
                        salesRepOptions
                            .find((u) => String(u.id) === String(id))
                            ?.label?.toLowerCase()
                            .includes(q)
                    ))
            );
        });
    }, [analyticsRows, globalSearch, salesRepOptions]);


    const offerTotals = useMemo(() => {
        const rows = Array.isArray(tableRows) ? tableRows : [];

        const sumUsd = rows.reduce((acc, r) => {
            const offer = Number(r.offer || 0);
            const cur = r.offerCurrency || "USD";
            // normalizeOffer -> USD'e çeviriyor (rate tabanlı)
            return acc + normalizeOffer(offer, cur);
        }, 0);

        // USD’den selectedCurrency’ye çevir (sadece total)
        const totalInSelected = convertOffer(sumUsd, "USD", selectedCurrency);

        return {
            sumUsd,
            totalInSelected,
            count: rows.filter((r) => Number(r.offer || 0) > 0).length,
        };
    }, [tableRows, selectedCurrency]);

    const tableRowsWithTotals = useMemo(() => {
        const rows = Array.isArray(tableRows) ? tableRows : [];

        return [
            ...rows,
            {
                id: "__totals__",
                _isTotals: true,
                offerTotals,
                selectedCurrency,
            },
        ];
    }, [tableRows, offerTotals, selectedCurrency]);


    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

    const statusOfferSums = useMemo(() => {
        const sums = {};
        Object.keys(STATUS_META).forEach((k) => (sums[k] = 0));

        (analyticsRows || []).forEach((r) => {
            const key = String(r.status || "pending").toLowerCase();
            const offer = Number(r.offer || 0);
            const cur = r.offerCurrency || "USD";

            // önce USD'e normalize, sonra seçili para birimine çevir
            const usd = normalizeOffer(offer, cur);
            const converted = convertOffer(usd, "USD", selectedCurrency);

            sums[key] = (sums[key] || 0) + converted;
        });

        return sums;
    }, [analyticsRows, selectedCurrency]);




    // ✅ Filtre ekleme
    const addFilter = (type, value) => {
        const safeType = String(type ?? "").trim();
        const safeValue = String(value ?? "").trim();

        if (!safeType || !safeValue) return;

        setChartFilters((prev) => {
            const cleanPrev = Array.isArray(prev)
                ? prev.filter((f) => f && f.type && f.value)
                : [];

            if (cleanPrev.some((f) => f.type === safeType && f.value === safeValue)) {
                return cleanPrev;
            }

            return [...cleanPrev, { type: safeType, value: safeValue }];
        });
    };


    // ✅ Filtre silme
    const removeFilter = (filterToRemove) => {
        if (!filterToRemove?.type || !filterToRemove?.value) return;

        setChartFilters((prev) =>
            (Array.isArray(prev) ? prev : [])
                .filter((f) => f && f.type && f.value)
                .filter((f) => !(f.type === filterToRemove.type && f.value === filterToRemove.value))
        );
    };


    const fmt3 = (n) =>
        Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });


// 🔢 Artış/azalış hesaplayan yardımcı fonksiyon
    const calcDiff = (current, prev, suffix = "") => {
        const diff = (Number(current || 0) - Number(prev || 0));
        const sign = diff >= 0 ? "+" : "";
        return `${sign}${fmt3(diff)} ${suffix} vs last month`;
    };


// 📅 Ay bazlı veriler (örnek olarak son iki ay karşılaştırması)
    const currentMonth = dayjs().format("YYYY-MM");
    const lastMonth = dayjs().subtract(1, "month").format("YYYY-MM");

    const rfqsByCreatedMonth = analyticsRows.reduce((acc, r) => {
        const month = r.created_at ? dayjs(r.created_at).format("YYYY-MM") : "Unknown";
        if (!acc[month]) acc[month] = { count: 0, customers: new Set(), origins: new Set(), destinations: new Set(), offers: [] };
        acc[month].count++;
        acc[month].customers.add(r.customer?.name);
        if (r.origin) acc[month].origins.add(r.origin);
        if (r.destination) acc[month].destinations.add(r.destination);
        if (r.offerConverted) acc[month].offers.push(r.offerConverted);
        return acc;
    }, {});

    const currentStats = rfqsByCreatedMonth[currentMonth] || { count: 0, customers: new Set(), origins: new Set(), destinations: new Set(), offers: [] };
    const lastStats = rfqsByCreatedMonth[lastMonth] || { count: 0, customers: new Set(), origins: new Set(), destinations: new Set(), offers: [] };

// 🎯 KPI kartları
    const kpiCards = [
        {
            title: "Total RFQs",
            value: totalRFQs,
            icon: <TrendingUpIcon fontSize="large" color="primary" />,
            trend: calcDiff(currentStats.count, lastStats.count, "RFQs"),
        },
        {
            title: `Avg Offer (${selectedCurrency})`,
            value: avgOffer,
            icon: <PublicIcon fontSize="large" color="success" />,
            trend: calcDiff(
                currentStats.offers.length
                    ? currentStats.offers.reduce((a, b) => a + b, 0) / currentStats.offers.length
                    : 0,
                lastStats.offers.length
                    ? lastStats.offers.reduce((a, b) => a + b, 0) / lastStats.offers.length
                    : 0,
                selectedCurrency
            ),
        },
        {
            title: "Unique Origins",
            value: uniqueOrigins,
            icon: <LocationOnIcon fontSize="large" color="warning" />,
            trend: calcDiff(currentStats.origins.size, lastStats.origins.size, "Origins"),
        },
        {
            title: "Unique Destinations",
            value: uniqueDestinations,
            icon: <FlightTakeoffIcon fontSize="large" color="secondary" />,
            trend: calcDiff(currentStats.destinations.size, lastStats.destinations.size, "Destinations"),
        },
        {
            title: "Unique Customers",
            value: [...new Set(analyticsRows.map((r) => r.customer?.name))].filter(Boolean).length,
            icon: <PeopleIcon fontSize="large" color="info" />,
            trend: calcDiff(currentStats.customers.size, lastStats.customers.size, "Customers"),
        },
    ];

    const chartOptionsWithTooltip = (onClickHandler) => ({
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 10, bottom: 10, left: 10, right: 10 } },

        // ✅ Pie slice click -> filtre
        onClick: (event, elements, chart) => {
            if (!elements || elements.length === 0) return;
            const index = elements[0]?.index;
            const labels = chart?.data?.labels;
            if (!labels || labels[index] == null) return;

            const label = String(labels[index] || "").trim();
            if (label && onClickHandler) onClickHandler(label);
        },

        plugins: {
            legend: {
                position: "bottom",
                align: "center",
                labels: {
                    boxWidth: 12,
                    boxHeight: 10,
                    padding: 6,
                    font: (ctx) => {
                        const chart = ctx.chart;
                        const labelCount = chart.data.labels.length;
                        const longestLabel = Math.max(...chart.data.labels.map((l) => String(l).length));
                        const baseSize = 12;
                        if (labelCount > 8 || longestLabel > 14) return { size: 10 };
                        if (labelCount > 6 || longestLabel > 12) return { size: 11 };
                        return { size: baseSize };
                    },
                    generateLabels: (chart) => {
                        const data = chart.data;
                        if (!data.labels.length) return [];
                        return data.labels.map((label, i) => {
                            const hidden = !chart.getDataVisibility(i);
                            const fullText = String(label);
                            const shortLabel = fullText.length > 10 ? fullText.slice(0, 10) + "…" : fullText;
                            return {
                                text: shortLabel,
                                fillStyle: data.datasets?.[0]?.backgroundColor?.[i],
                                strokeStyle: data.datasets?.[0]?.backgroundColor?.[i],
                                hidden,
                                index: i,
                                fullText,
                            };
                        });
                    },
                },
                onClick: (e, legendItem, legend) => {
                    const chart = legend.chart;
                    chart.toggleDataVisibility(legendItem.index);
                    chart.update();
                },
                onHover: (event, legendItem) => {
                    let tooltip = document.getElementById("legend-tooltip");
                    if (!tooltip) {
                        tooltip = document.createElement("div");
                        tooltip.id = "legend-tooltip";
                        tooltip.style.position = "absolute";
                        tooltip.style.background = "rgba(0,0,0,0.75)";
                        tooltip.style.color = "#fff";
                        tooltip.style.padding = "4px 8px";
                        tooltip.style.borderRadius = "4px";
                        tooltip.style.fontSize = "12px";
                        tooltip.style.pointerEvents = "none";
                        document.body.appendChild(tooltip);
                    }
                    tooltip.innerText = legendItem.fullText;
                    tooltip.style.left = `${event.x + 10}px`;
                    tooltip.style.top = `${event.y - 25}px`;
                },
                onLeave: () => {
                    const tooltip = document.getElementById("legend-tooltip");
                    if (tooltip) tooltip.remove();
                },
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems) =>
                        tooltipItems[0].chart.data.labels[tooltipItems[0].dataIndex],
                },
            },
        },
    });

    const topCustomers = useMemo(() => {
        const counts = {};

        analyticsRows.forEach((r) => {
            const name = r.customer?.name;
            if (!name) return;

            counts[name] = (counts[name] || 0) + 1;
        });

        return Object.entries(counts)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    }, [analyticsRows]);


    const requestedMonths = useMemo(() => {
        const map = {};

        analyticsRows.forEach((r) => {
            if (!r.etd) return;
            const m = dayjs(r.etd).format("YYYY-MM");
            map[m] = (map[m] || 0) + 1;
        });

        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [analyticsRows]);
    const requestedWeeks = useMemo(() => {
        const map = {};
        analyticsRows.forEach((r) => {
            if (!r.etd) return;
            const w = dayjs(r.etd).format("GGGG-[W]WW"); // örn: 2026-W06
            map[w] = (map[w] || 0) + 1;
        });

        // Son 12 hafta gösterelim (veri yoksa da label üretmek istersen söyle, eklerim)
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .slice(-12);
    }, [analyticsRows]);


    useEffect(() => {
        if (chartFilters.length === 0) {
            setSelectedMonth(null);
        }
    }, [chartFilters]);

    const tableRowsSorted = useMemo(() => {
        return [...tableRows].sort((a, b) => {
            const da = a?.etd ? dayjs(a.etd).valueOf() : -Infinity;
            const db = b?.etd ? dayjs(b.etd).valueOf() : -Infinity;
            return db - da; // DESC
        });
    }, [tableRows]);


    const content = (
        <MDBox py={embedded ? 0 : 3} px={embedded ? 0 : 2} sx={{ width: "100%" }}>
            {loading ? (
                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="60vh"
                >
                    <CircularProgress />
                </MDBox>
            ) : (
                    <MDBox sx={{ width: "100%", minWidth: 0 }}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="60vh"
                            >
                                <CircularProgress />
                            </MDBox>
                        ) : (
                            <Grid container spacing={4}>
                                {/* KPI Kartları */}
                                <Grid item xs={12}>
                                    <Grid container spacing={4}>
                                        {kpiCards.map((kpi, i) => (
                                            <Grid item xs={12} sm={6} md={2.4} key={i}>
                                                <Card sx={{ p: 3, borderRadius: "10px", boxShadow: "none" }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                                        {kpi.icon}
                                                        <MDTypography variant="h6" color="text">
                                                            {kpi.title}
                                                        </MDTypography>
                                                    </Box>
                                                    <MDTypography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                                                        {kpi.value}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="text">
                                                        {kpi.trend}
                                                    </MDTypography>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>

                                {/* 📊 RFQs by Status */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            RFQs by Status
                                        </MDTypography>

                                        {/* ✅ 2 kolon: Sol donut, sağ status offer */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 2,
                                                alignItems: "center",
                                                height: 290, // Card(350) - title/padding alanı
                                            }}
                                        >
                                            {/* SOL: Donut (kÜÇÜLMESİN) */}
                                            <Box sx={{ flex: "1 1 0", height: 260, minWidth: 140 }}>
                                                <Doughnut
                                                    data={{
                                                        labels: Object.keys(STATUS_META),
                                                        datasets: [
                                                            {
                                                                label: "RFQs",
                                                                data: Object.keys(STATUS_META).map((s) =>
                                                                    analyticsRows.filter(
                                                                        (r) => String(r.status || "").toLowerCase() === s
                                                                    ).length
                                                                ),
                                                                backgroundColor: Object.keys(STATUS_META).map((s) => STATUS_META[s].color),
                                                                borderWidth: 2,
                                                            },
                                                        ],
                                                    }}
                                                    options={{
                                                        ...chartOptions("doughnut", (label) => addFilter("status", label)),
                                                        plugins: {
                                                            ...(chartOptions("doughnut", (label) => addFilter("status", label)).plugins || {}),
                                                            legend: { display: false }, // ✅ üst legend kapalı
                                                        },
                                                    }}
                                                />
                                            </Box>

                                            {/* SAG: Status Offerler (alt alta) */}
                                            <Box
                                                sx={{
                                                    flex: "0 1 210px",
                                                    minWidth: 0,
                                                    height: 260,
                                                    overflowY: "auto",
                                                    pr: 0.5,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 1,
                                                }}
                                            >
                                                {Object.keys(STATUS_META).map((key) => {
                                                    const meta = STATUS_META[key];
                                                    const sum = statusOfferSums?.[key] ?? 0;

                                                    return (
                                                        <Chip
                                                            key={key}
                                                            size="small"
                                                            onClick={() => addFilter("status", key)}
                                                            icon={<StatusDot status={key} />}
                                                            label={`${meta.label}: ${fmtMoney(sum)} ${selectedCurrency}`}
                                                            sx={{
                                                                borderRadius: 2,
                                                                fontWeight: 700,
                                                                cursor: "pointer",
                                                                justifyContent: "flex-start",
                                                                bgcolor: alpha(meta.color, 0.12),
                                                                color: meta.color,
                                                                "& .MuiChip-icon": { color: meta.color },
                                                                "& .MuiChip-label": { width: "100%", textAlign: "left" },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>


                                {/* 📊 RFQs by Service Mode */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            RFQs by Service Mode
                                        </MDTypography>
                                        <Doughnut
                                            data={{
                                                labels: [
                                                    ...new Set(analyticsRows.map((r) => r.serviceMode || "Unknown")),
                                                ],
                                                datasets: [
                                                    {
                                                        label: "RFQs",
                                                        data: [
                                                            ...new Set(analyticsRows.map((r) => r.serviceMode || "Unknown")),
                                                        ].map(
                                                            (mode) =>
                                                                analyticsRows.filter((r) => r.serviceMode === mode).length
                                                        ),
                                                        backgroundColor: ["#42a5f5", "#66bb6a", "#ffa726", "#ab47bc", "#ef5350"],
                                                        borderWidth: 2,
                                                    },
                                                ],
                                            }}
                                            options={chartOptions("doughnut", (label) => addFilter("serviceMode", label))}
                                        />
                                    </Card>
                                </Grid>

                                {/* 📊 RFQs by Transportation Mode */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            RFQs by Transportation Mode
                                        </MDTypography>

                                        <Doughnut
                                            data={{
                                                labels: [
                                                    ...new Set(
                                                        analyticsRows.map((r) => r.transportationMode || "Unknown")
                                                    ),
                                                ],
                                                datasets: [
                                                    {
                                                        label: "RFQs",
                                                        data: [
                                                            ...new Set(
                                                                analyticsRows.map((r) => r.transportationMode || "Unknown")
                                                            ),
                                                        ].map(
                                                            (mode) =>
                                                                analyticsRows.filter((r) => (r.transportationMode || "Unknown") === mode).length
                                                        ),
                                                        backgroundColor: ["#42a5f5", "#66bb6a", "#ffa726", "#ab47bc", "#ef5350"],
                                                        borderWidth: 2,
                                                    },
                                                ],
                                            }}
                                            options={chartOptions("doughnut", (label) =>
                                                addFilter("transportationMode", label)
                                            )}
                                        />
                                    </Card>
                                </Grid>


                                {/* 📊 Top Origins */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            Top Origins
                                        </MDTypography>
                                        <Doughnut
                                            data={{
                                                labels: topOrigins.map((o) => o.label),
                                                datasets: [
                                                    {
                                                        label: "RFQs",
                                                        data: topOrigins.map((o) => o.count),
                                                        backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF"],
                                                        borderWidth: 2,
                                                    },
                                                ],
                                            }}
                                            options={chartOptionsWithTooltip((label) => addFilter("origin", label))}
                                        />
                                    </Card>
                                </Grid>

                                {/* 📊 Top Destinations */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            Top Destinations
                                        </MDTypography>
                                        <Doughnut
                                            data={{
                                                labels: topDestinations.map((d) => d.label),
                                                datasets: [
                                                    {
                                                        label: "RFQs",
                                                        data: topDestinations.map((d) => d.count),
                                                        backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF"],
                                                        borderWidth: 2,
                                                    },
                                                ],
                                            }}
                                            options={chartOptionsWithTooltip((label) => addFilter("destination", label))}
                                        />
                                    </Card>
                                </Grid>
                                {/* 📊 Top Owners */}
                                <Grid item xs={12} md={6} lg={4}>
                                    <Card sx={{ p: 3, height: 350, borderRadius: "10px", boxShadow: "none" }}>
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            RFQs by Owner
                                        </MDTypography>

                                        <Doughnut
                                            data={{
                                                labels: topOwners.map((o) => o.label),
                                                datasets: [
                                                    {
                                                        label: "RFQs",
                                                        data: topOwners.map((o) => o.count),
                                                        backgroundColor: [
                                                            "#42a5f5",
                                                            "#66bb6a",
                                                            "#ffa726",
                                                            "#ab47bc",
                                                            "#ef5350",
                                                            "#26c6da",
                                                        ],
                                                        borderWidth: 2,
                                                    },
                                                ],
                                            }}
                                            options={chartOptionsWithTooltip((label) =>
                                                addFilter("owner", label)
                                            )}
                                        />
                                    </Card>
                                </Grid>
                                {/* 📊 Top Customers */}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: 360,
                                            borderRadius: "10px",
                                            boxShadow: "none",
                                            overflow: "hidden",          // 🔥 KRİTİK
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <MDTypography variant="h6" sx={{ mb: 2 }}>
                                            Top Customers (RFQ Count)
                                        </MDTypography>

                                        {/* 🔥 Chart wrapper */}
                                        <Box sx={{ flex: 1, minHeight: 0 }}>
                                            <Bar
                                                data={{
                                                    labels: topCustomers.map((c) => c.label),
                                                    datasets: [
                                                        {
                                                            label: "RFQs",
                                                            data: topCustomers.map((c) => c.count),
                                                            borderRadius: 8,
                                                        },
                                                    ],
                                                }}
                                                options={{
                                                    indexAxis: "y",
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { display: false },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: (ctx) =>
                                                                    `${ctx.label} : ${ctx.parsed.x} RFQs`,
                                                            },
                                                        },
                                                    },
                                                    scales: {
                                                        x: {
                                                            grid: { color: "#eee" },
                                                            ticks: {
                                                                stepSize: 1,
                                                            },
                                                        },
                                                        y: {
                                                            grid: { display: false },
                                                            ticks: {
                                                                autoSkip: true,       // 🔥
                                                                maxTicksLimit: 6,     // 🔥
                                                                callback: function (value) {
                                                                    const label = this.getLabelForValue(value);
                                                                    return label.length > 30
                                                                        ? label.slice(0, 30) + "…"
                                                                        : label;
                                                                },
                                                            },
                                                        },
                                                    },

                                                    // 🔥 CLICK → CUSTOMER FILTER
                                                    onClick: (event, elements, chart) => {
                                                        if (!elements?.length) return;
                                                        const index = elements[0].index;
                                                        const label = chart.data.labels[index];
                                                        if (label) addFilter("customer", label);
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Card>
                                </Grid>


                                <Grid item xs={12} md={6}>
                                    <Card    sx={{
                                        p: 3,
                                        height: 360,
                                        borderRadius: "10px",
                                        boxShadow: "none",
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}>
                                        <MDTypography variant="h6" sx={{ mb: 1 }}>
                                            RFQs by Requested Date
                                        </MDTypography>
                                        <Box sx={{ flex: 1, minHeight: 0}}>
                                            <Line
                                                data={{
                                                    labels: requestedMonths.map((m) => m.label),
                                                    datasets: [
                                                        {
                                                            label: "RFQs",
                                                            data: requestedMonths.map((m) => m.count),
                                                            tension: 0.35,
                                                            borderWidth: 3,
                                                            fill: false,
                                                            pointRadius: requestedMonths.map((m) =>
                                                                m.label === selectedMonth ? 7 : 4
                                                            ),
                                                            pointBackgroundColor: requestedMonths.map((m) =>
                                                                m.label === selectedMonth ? "#E53935" : "#1E88E5"
                                                            ),
                                                        },
                                                    ],

                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { display: false },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: (ctx) =>
                                                                    `${ctx.label} : ${ctx.parsed.y} RFQs`,
                                                            },
                                                        },
                                                    },
                                                    scales: {
                                                        x: {
                                                            grid: { display: false },
                                                            ticks: {
                                                                autoSkip: true,
                                                                maxTicksLimit: 8,
                                                                maxRotation: 45,
                                                                minRotation: 45,
                                                            },
                                                        },
                                                        y: {
                                                            grid: { color: "#eee" },
                                                            ticks: { stepSize: 1 },
                                                        },
                                                    },

                                                    // 🔥 CLICK → MONTH FILTER
                                                    onClick: (event, elements, chart) => {
                                                        if (!elements?.length) return;
                                                        const index = elements[0].index;
                                                        const label = chart.data.labels[index];
                                                        if (label) {
                                                            setSelectedMonth(label);
                                                            addFilter("requestedMonth", label);
                                                        }
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: 360,
                                            borderRadius: "10px",
                                            boxShadow: "none",
                                            overflow: "hidden",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <MDTypography variant="h6" sx={{ mb: 1 }}>
                                            RFQs by Requested Week
                                        </MDTypography>

                                        <Box sx={{ flex: 1, minHeight: 0 }}>
                                            <Bar
                                                data={{
                                                    labels: requestedWeeks.map((w) => w.label),
                                                    datasets: [
                                                        {
                                                            label: "RFQs",
                                                            data: requestedWeeks.map((w) => w.count),
                                                            borderRadius: 8,
                                                        },
                                                    ],
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: { legend: { display: false } },
                                                    scales: {
                                                        x: { grid: { display: false } },
                                                        y: { grid: { color: "#eee" }, ticks: { stepSize: 1 } },
                                                    },
                                                    // ✅ CLICK -> weekly filter
                                                    onClick: (event, elements, chart) => {
                                                        if (!elements?.length) return;
                                                        const index = elements[0].index;
                                                        const label = chart.data.labels[index];
                                                        if (label) addFilter("requestedWeek", label);
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Card>
                                </Grid>
                                {/* 🗂 RFQ Tablosu */}
                                {/* 🗂 RFQ Tablosu */}
                                <Grid item xs={12}>
                                    <Card sx={{ p: 3, borderRadius: "10px", boxShadow: "none" }}>
                                        {/* Filtre Chipleri */}
                                        {chartFilters.length > 0 && (
                                            <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                                {(chartFilters || [])
                                                    .filter(Boolean)
                                                    .map((f, idx) => {
                                                        const t = String(f?.type ?? "").trim();
                                                        const v = String(f?.value ?? "").trim();
                                                        if (!t || !v) return null;

                                                        return (
                                                            <Chip
                                                                key={`${t}-${v}-${idx}`}
                                                                label={`${t}: ${v}`}
                                                                onDelete={() => removeFilter({ type: t, value: v })}
                                                                color={safeChipColor(chipColor(t))}
                                                                size="small"
                                                                icon={<SearchIcon />}
                                                                sx={{ borderRadius: 2, fontWeight: 700 }}
                                                            />
                                                        );
                                                    })}

                                                <Button
                                                    size="small"
                                                    onClick={() => setChartFilters([])}
                                                    sx={{ textTransform: "none", fontWeight: 700 }}
                                                >
                                                    Clear All
                                                </Button>
                                            </Box>
                                        )}

                                        {/* HEADER BAR */}
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            mb={2}
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={2}
                                        >
                                            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                                <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                                                    RFQ List
                                                </MDTypography>
                                            </Box>

                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                {/* Currency */}
                                                <FormControl
                                                    size="small"
                                                    sx={{
                                                        width: 200,
                                                        "& .MuiInputLabel-root": {
                                                            fontWeight: 700,
                                                        },
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                            height: 55,
                                                            width: 200,
                                                            fontWeight: 700,
                                                            alignItems: "center",
                                                        },
                                                        "& .MuiSelect-select": {
                                                            height: "55px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            paddingTop: 0,
                                                            paddingBottom: 0,
                                                        },
                                                    }}
                                                >
                                                    <InputLabel id="currency-label">Currency</InputLabel>

                                                    <Select
                                                        labelId="currency-label"
                                                        value={selectedCurrency}
                                                        label="Currency"
                                                        onChange={(e) => setSelectedCurrency(e.target.value)}
                                                        sx={{ width: 200, height: 55 }}
                                                        renderValue={(val) => (
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <CurrencyExchangeIcon fontSize="small" />
                                                                <span style={{ fontWeight: 700, letterSpacing: 0.5 }}>{val}</span>
                                                            </Box>
                                                        )}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                sx: {
                                                                    borderRadius: 2,
                                                                    mt: 1,
                                                                    boxShadow: "none",
                                                                    "& .MuiMenuItem-root": {
                                                                        fontWeight: 700,
                                                                        borderRadius: 1.5,
                                                                        mx: 0.8,
                                                                        my: 0.2,
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="USD">USD</MenuItem>
                                                        <MenuItem value="EUR">EUR</MenuItem>
                                                        <MenuItem value="GBP">GBP</MenuItem>
                                                        <MenuItem value="TRY">TRY</MenuItem>
                                                    </Select>
                                                </FormControl>


                                                {/* Date Start */}
                                                <TextField
                                                    size="small"
                                                    label="RFQ Date Start"
                                                    type="date"
                                                    value={rfqDateStart}
                                                    onChange={(e) => setRfqDateStart(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{
                                                        minWidth: 170,
                                                        "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 },
                                                    }}
                                                />

                                                {/* Date End */}
                                                <TextField
                                                    size="small"
                                                    label="RFQ Date End"
                                                    type="date"
                                                    value={rfqDateEnd}
                                                    onChange={(e) => setRfqDateEnd(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{
                                                        minWidth: 170,
                                                        "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 },
                                                    }}
                                                />

                                                {/* Search */}
                                                <TextField
                                                    size="small"
                                                    placeholder="Search..."
                                                    value={globalSearch}
                                                    onChange={(e) => setGlobalSearch(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        minWidth: 220,
                                                        "& .MuiOutlinedInput-root": { borderRadius: 2, height: 46 },
                                                    }}
                                                />

                                                {(rfqDateStart || rfqDateEnd) && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setRfqDateStart("");
                                                            setRfqDateEnd("");
                                                        }}
                                                        sx={{ textTransform: "none", fontWeight: 700 }}
                                                    >
                                                        Clear Dates
                                                    </Button>
                                                )}
                                            </Box>
                                        </MDBox>

                                        {/* DATATABLE (Totals row DataTable içinde sticky) */}
                                        <DataTable
                                            table={{
                                                columns,
                                                rows: tableRowsWithTotals, // ✅ totals row burada
                                                initialState: { sortBy: [{ id: "etd", desc: true }] },
                                            }}
                                            entriesPerPage={{ defaultValue: 100 }}
                                            showTotalEntries
                                            isSorted
                                            sx={{
                                                // header sticky
                                                "& thead": { position: "sticky", top: 0, zIndex: 3 },
                                                "& thead tr th": {
                                                    backgroundColor: "#fafafa",
                                                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                                                },

                                                // cell overflow
                                                "& .MuiTableCell-root": {
                                                    whiteSpace: "nowrap",
                                                    textOverflow: "ellipsis",
                                                    overflow: "hidden",
                                                    maxWidth: 240,
                                                },

                                                // hover row
                                                "& tbody tr:hover": { backgroundColor: "#f7f9fb" },

                                                // totals row (last)
                                                "& tbody tr:last-of-type": {
                                                    background:
                                                        "rgba(30,136,229,0.08)",
                                                },

                                                // totals row sticky footer
                                                "& tbody tr:last-of-type td": {
                                                    position: "sticky",
                                                    bottom: 0,
                                                    zIndex: 4,
                                                    background:
                                                        "rgba(30,136,229,0.10)",
                                                    borderTop: "2px solid rgba(0,0,0,0.10)",
                                                    fontWeight: 950,
                                                },
                                            }}
                                        />
                                    </Card>
                                    {requestForm && (
                                        <RfqFormDialog
                                            open={openDialog}
                                            onClose={() => setOpenDialog(false)}
                                            form={requestForm}
                                            setForm={setRequestForm}
                                            onSubmit={async (form) => {
                                                const customerData = selectedCustomer
                                                    ? {
                                                        id: selectedCustomer.id || selectedCustomer._id,
                                                        name: selectedCustomer.name || selectedCustomer.companyName || selectedCustomer.label || "",
                                                    }
                                                    : form.customer || null;

                                                const payload = {
                                                    ...form,
                                                    customer: customerData,
                                                    request: {
                                                        ...form,
                                                        customer: customerData,
                                                    },
                                                };

                                                const res = await CrudService.updateSentRequest(form.id, payload);

                                                const updated =
                                                    res?.data?.data?.attributes ||
                                                    res?.data?.attributes ||
                                                    res?.data?.data ||
                                                    res?.data ||
                                                    payload;

                                                const normalizedUpdated = {
                                                    ...form,
                                                    ...updated,
                                                    ...(updated.request || {}),
                                                    id: String(form.id),
                                                    rfqNumber: updated.rfqNumber || form.rfqNumber || "—",
                                                    customer: updated.customer || customerData,
                                                    request: updated.request || payload.request,
                                                    offer: updated.offer ?? form.offer,
                                                    offerCurrency: updated.offerCurrency || form.offerCurrency || "USD",
                                                    offerUsd: normalizeOffer(
                                                        Number(updated.offer ?? form.offer ?? 0),
                                                        updated.offerCurrency || form.offerCurrency || "USD"
                                                    ),
                                                    status: updated.status || form.status,
                                                    origin: updated.origin || form.origin,
                                                    destination: updated.destination || form.destination,
                                                    serviceMode: updated.serviceMode || form.serviceMode,
                                                    transportationMode: updated.transportationMode || form.transportationMode,
                                                    salesRepIds: updated.salesRepIds || form.salesRepIds || [],
                                                };

                                                setRfqList((prev) =>
                                                    prev.map((r) =>
                                                        String(r.id) === String(form.id)
                                                            ? { ...r, ...normalizedUpdated }
                                                            : r
                                                    )
                                                );

                                                setRequestForm(normalizedUpdated);
                                                setOpenDialog(false);
                                            }}
                                            onConnectQuote={handleConnectQuote}
                                            onNewCustomer={() => navigate("/customer/create-customer/details")}
                                            customerOptions={customerOptions}
                                            selectedCustomer={selectedCustomer}
                                            setSelectedCustomer={setSelectedCustomer}
                                            salesRepOptions={salesRepOptions}
                                            countryOptions={countryOptions}
                                            getCountryLabel={getCountryLabel}
                                            canonicalCountry={canonicalCountry}
                                            findCountryOption={findCountryOption}
                                            originKind={originKind}
                                            destinationKind={destinationKind}
                                            originOptions={originOptions}
                                            destOptions={destOptions}
                                            originLoading={originLoading}
                                            destLoading={destLoading}
                                            STATUS_META={STATUS_META}
                                            StatusDot={StatusDot}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </MDBox>
            )}
        </MDBox>
    );

    if (embedded) return content;

    return <DashboardLayout>{content}</DashboardLayout>;

}
